import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Pencil, Phone, Trash2 } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { formatMoney, formatRelative } from '@shared/lib/format';
import { toast } from '@shared/ui/feedback/Toast';
import { cn } from '@shared/lib/cn';
import { currentMemberId, fetchClassified, deleteClassified, type ClassifiedDetail as Classified } from './api';

function statusLabel(status: string): string {
  const s = status.toUpperCase();
  if (s === 'PENDING') return 'Pending approval';
  if (s === 'APPROVED') return 'Approved';
  if (s === 'DECLINED') return 'Declined';
  if (s === 'CLOSED') return 'Closed';
  return status;
}

function formatExpiry(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function mailtoHref(email: string): string {
  return `mailto:${encodeURIComponent(email.trim())}`;
}

function telHref(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, '');
  return `tel:${cleaned}`;
}

function hasContactSection(c: Classified): boolean {
  return (
    (c.show_name && !!c.member_name) ||
    (c.show_email && !!c.member_email) ||
    (c.show_phone && !!c.member_phone)
  );
}

/**
 * Classified listing detail — member-portal parity: back nav, metadata,
 * contact links (name / email / phone), owner edit/delete.
 */
export function ClassifiedDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeImage, setActiveImage] = useState(0);

  const classifiedQuery = useQuery({
    queryKey: ['classified', id],
    queryFn: () => fetchClassified(id ?? ''),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteClassified(id ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classifieds'] });
      toast.success('Listing removed');
      navigate('/app/classifieds', { replace: true });
    },
    onError: (e) => toast.danger("Couldn't delete", e instanceof Error ? e.message : undefined),
  });

  const galleryOnly = useMemo(() => {
    const c = classifiedQuery.data;
    if (!c) return [];
    const cover = c.cover_image_url;
    return c.images.filter((img) => img.url !== cover && img.id !== 'profile');
  }, [classifiedQuery.data]);

  if (classifiedQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <Skeleton className="aspect-[4/3] rounded-2xl" />
        <Skeleton className="h-9 w-2/3 mt-4" />
        <div className="space-y-2 mt-3">
          <Skeleton className="h-3" />
          <Skeleton className="h-3" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      </div>
    );
  }

  if (classifiedQuery.isError) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <button
          type="button"
          onClick={() => navigate('/app/classifieds')}
          className="mb-4 text-body-sm font-medium text-primary-700"
        >
          ← Back to classifieds
        </button>
        <ErrorState onRetry={() => classifiedQuery.refetch()} />
      </div>
    );
  }

  const c = classifiedQuery.data;
  if (!c) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <EmptyState title="Listing not found." />
      </div>
    );
  }

  const memberId = currentMemberId();
  const userOwns = !!memberId && !!c.member_id && c.member_id === memberId;
  const coverUrl = c.cover_image_url ?? c.images[0]?.url;
  const showContact = !userOwns && hasContactSection(c);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        backTo="/app/classifieds"
        backLabel="Back to classifieds"
        backBehavior="parent"
        eyebrow="Classified"
        title={c.title}
        titleClassName="mt-1 leading-tight"
      >
        <p className="mt-1.5 text-body-sm text-white/70">
          Posted {formatRelative(c.posted_at)}
          {c.author_name ? ` · ${c.author_name}` : ''}
        </p>
      </HeroHeader>

      <div className="flex flex-col gap-6 px-5 pt-5 pb-safe-or-6">
        {userOwns ? (
          <span
            className={cn(
              'self-start rounded-pill px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide',
              c.status === 'APPROVED' && 'bg-success/15 text-success',
              c.status === 'PENDING' && 'bg-brass/20 text-brass',
              c.status === 'DECLINED' && 'bg-danger/15 text-danger',
              c.status === 'CLOSED' && 'bg-sunken text-ink-muted',
            )}
          >
            {statusLabel(c.status)}
          </span>
        ) : null}

        {coverUrl ? (
          <>
            <div className="aspect-[4/3] -mx-5 overflow-hidden bg-sunken">
              <img src={c.images[activeImage]?.url ?? coverUrl} alt={c.title} className="h-full w-full object-cover" />
            </div>
            {c.images.length > 1 ? (
              <ul className="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5">
                {c.images.map((img, i) => (
                  <li key={img.id}>
                    <button
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={cn(
                        'h-16 w-16 flex-shrink-0 overflow-hidden rounded-md hairline',
                        i === activeImage && 'ring-2 ring-primary-600',
                      )}
                    >
                      <img src={img.url} alt="" className="h-full w-full object-cover" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
        <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
            Details
        </h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-body">
         
          <dt className="text-ink-muted">Price</dt>
          <dd className="font-display text-display-md text-ink tabular">
            {c.price !== undefined ? formatMoney(c.price, { currency: c.currency }) : 'POA'}
            {c.currency ? (
              <span className="ml-1 text-body-sm text-ink-muted">({c.currency})</span>
            ) : null}
          </dd>
          <dt className="text-ink-muted">Expires</dt>
          <dd>{formatExpiry(c.expiry)}</dd>
          {c.category ? (
            <>
              <dt className="text-ink-muted">Category</dt>
              <dd>{c.category}</dd>
            </>
          ) : null}
        </dl>

        {c.description ? (
          <section>
            <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
              Description
            </h2>
            <p className="mt-2 whitespace-pre-line text-body text-ink">{c.description}</p>
          </section>
        ) : null}

        {galleryOnly.length > 0 ? (
          <section>
            <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
              More photos
            </h2>
            <ul className="mt-3 grid grid-cols-3 gap-2">
              {galleryOnly.map((img) => (
                <li key={img.id}>
                  <img src={img.url} alt="" className="aspect-square w-full rounded-md object-cover hairline" />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {showContact ? (
          <section>
            <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
              Contact Info
            </h2>
            <ul className="mt-3 flex flex-col gap-3 text-body">
              {c.show_name && c.member_name ? (
                <li>
                  <span className="text-ink-muted">Name: </span>
                  {c.member_name}
                </li>
              ) : null}
              {c.show_email && c.member_email ? (
                <li className="flex flex-wrap items-baseline gap-1">
                  <span className="text-ink-muted">Email: </span>
                  <a
                    href={mailtoHref(c.member_email)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary-700 underline-offset-2 hover:underline"
                  >
                    {c.member_email}
                  </a>
                </li>
              ) : null}
              {c.show_phone && c.member_phone ? (
                <li className="flex flex-wrap items-baseline gap-1">
                  <span className="text-ink-muted">Phone: </span>
                  <a
                    href={telHref(c.member_phone)}
                    className="font-medium text-primary-700 underline-offset-2 hover:underline"
                  >
                    {c.member_phone}
                  </a>
                </li>
              ) : null}
            </ul>
            <div className="mt-4 flex flex-col gap-2">
              {c.show_email && c.member_email ? (
                <ActionButton onClick={() => { window.location.href = mailtoHref(c.member_email!); }}>
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                  Email seller
                </ActionButton>
              ) : null}
              {c.show_phone && c.member_phone ? (
                <ActionButton variant="secondary" onClick={() => { window.location.href = telHref(c.member_phone!); }}>
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                  Call seller
                </ActionButton>
              ) : null}
            </div>
          </section>
        ) : null}

        {userOwns ? (
          <div className="flex gap-2 pt-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => navigate(`/app/classifieds/${c.classified_id}/edit`)}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
              Edit
            </Button>
            <Button
              variant="ghost"
              className="flex-1 text-danger"
              onClick={() => {
                if (confirm('Remove this listing?')) deleteMutation.mutate();
              }}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              Delete
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
